import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { stepHintKeys } from '@d-orca/shared/client/activities';
import type { ISchedulerTask, SiteId, TaskId, TaskStatus } from '@interfaces/IScheduler';
import { Button } from '@components/ui/Button/Button';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { useSchedulerActions, useSchedulerFrozenTasks } from '../../hooks/useSchedulerStore';
import { useTaskStep, type TaskStep } from '../../hooks/useTaskStep';
import { useTaskLifecycleActions } from '../../hooks/useTaskLifecycleActions';
import { useTaskCommitActions } from '../../hooks/useTaskCommitActions';
import { useTaskFinish } from '../../hooks/useTaskFinish';
import { FinishTaskDialog } from '../FinishTaskDialog/FinishTaskDialog';
import { useSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import { useSchedulerPortalContainer } from '../../context/SchedulerPortalContainer';
import { takeBackImpact, takeBackImpactLine, type ITakeBackImpact } from '../../lib/takeBackImpact';
import styles from './TaskCommitBlock.module.css';

// ---------------------------------------------------------------------------
// The task's STEP — the one action its position on the ladder calls for:
// commit, commit all, unblock, mark done, re-open (2026-09-18 commit UX
// design, "Drawer", amended 2026-09-23). It is told in two places:
//   · `TaskStepActions` — the buttons, in the drawer's sticky footer. The
//     promise rows read as a form and this is their submit, so it comes AFTER
//     them, and the footer keeps it in view however long the rows or the
//     progress board run. It stands where Save stands on the Edit tab.
//   · `TaskStepHint` — what stands between the task and that step ("Ready to
//     commit", "1 thing to fill in"), under the rows it sums up.
// Both read `useTaskStep` (hooks/useTaskStep.ts), so the words and the buttons
// cannot disagree about the state the task is in; the words themselves are the
// shared `stepHintKeys`, which the mobile sheet reads too.
// ---------------------------------------------------------------------------

interface ITaskStepHintProps {
  task: ISchedulerTask;
  step: TaskStep;
}

export function TaskStepHint({ task, step }: ITaskStepHintProps) {
  const { t } = useTranslation();
  const hints = stepHintKeys(task, step);
  if (hints.length === 0) return null;
  return (
    <p className={styles.hint}>
      {hints.map((hint) => (
        <span key={hint.key} className={hint.good ? styles.good : undefined}>{t(hint.key, hint.params)}</span>
      ))}
    </p>
  );
}

interface ITaskStepActionsProps {
  siteId: SiteId;
  taskId: TaskId;
  scheduleNumber?: string;
  /** What the footer holds when there is no step to take — a parent whose
   *  every leaf is already committed. */
  fallback: ReactNode;
}

export function TaskStepActions({ siteId, taskId, scheduleNumber, fallback }: ITaskStepActionsProps) {
  const { t } = useTranslation();
  const state = useTaskStep(siteId, taskId);
  const { setStatus } = useTaskLifecycleActions(siteId, taskId, scheduleNumber);
  const { openCommitDialog } = useSchedulerActions();
  const { uncommit, pending } = useTaskCommitActions({ siteId, taskId, scheduleNumber });
  // Mark done asks when it is not the planned end day (2026-09-22 amendment).
  const finish = useTaskFinish({ siteId, taskId, scheduleNumber });
  const frozen = useSchedulerFrozenTasks(siteId);
  const portalContainer = useSchedulerPortalContainer();
  // Take back deletes what was written on the promise — asked first, with
  // the counts, whenever there is something to lose (F-56).
  const [confirmTakeBack, setConfirmTakeBack] = useState<ITakeBackImpact | null>(null);
  if (!state) return null;

  const { task, step } = state;
  const { question } = finish;
  const takeBack = () => {
    const impact = takeBackImpact(task);
    if (impact) setConfirmTakeBack(impact);
    else void uncommit();
  };
  return (
    <>
      <StepButtons
        step={step}
        pending={pending}
        fallback={fallback}
        onCommit={() => openCommitDialog(task.id)}
        onStatus={setStatus}
        onDone={() => finish.requestDone(task)}
        onTakeBack={takeBack}
      />
      <DiscardModal
        open={confirmTakeBack !== null}
        title={t('scheduler.commit.takeBack.title')}
        description={confirmTakeBack ? takeBackImpactLine(confirmTakeBack, t) : undefined}
        confirmLabel={t('scheduler.commit.uncommitAction')}
        loading={pending}
        container={portalContainer}
        onCancel={() => setConfirmTakeBack(null)}
        onConfirm={() => {
          setConfirmTakeBack(null);
          void uncommit();
        }}
      />
      {question && (
        <FinishTaskDialog
          question={question}
          frozen={frozen}
          pending={finish.pending}
          onConfirm={(spans) => void finish.confirm(question, spans)}
          onClose={finish.dismiss}
        />
      )}
    </>
  );
}

interface IStepButtonsProps {
  step: TaskStep;
  pending: boolean;
  fallback: ReactNode;
  onCommit: () => void;
  onStatus: (status: TaskStatus) => void;
  /** Mark done — which may first ask when the work finished. */
  onDone: () => void;
  onTakeBack: () => void;
}

// The step is always the `md` accent button, rightmost — where Save stands on
// the Edit tab, in the same blue as Save and the toolbar's Add task — so the
// drawer's one decision weighs the same in every state. Going BACK (Re-open)
// is an outline, not the accent. The side exits (Block, Take back) sit left of
// it as small ghosts: findable, never a peer of the step.
function StepButtons({ step, pending, fallback, onCommit, onStatus, onDone, onTakeBack }: IStepButtonsProps) {
  const { t } = useTranslation();
  // Every step moves the task along the ladder — all of it `tasks.execute`; a
  // user without it gets the footer's fallback (Close) and no step button.
  const { canExecute } = useSchedulerCapabilities();
  if (!canExecute) return <>{fallback}</>;
  switch (step.kind) {
    case 'allCommitted':
      return <>{fallback}</>;
    case 'commitAll':
      return (
        <Button variant="accent" size="md" onClick={onCommit} data-testid="ladder-commit-all">
          {t('scheduler.commit.actionAll', { count: step.open })}
        </Button>
      );
    case 'milestone':
      return (
        <Button
          variant={step.done ? 'secondary' : 'accent'}
          size="md"
          onClick={step.done ? () => onStatus('planned') : onDone}
          data-testid="milestone-done"
        >
          {t(step.done ? 'scheduler.panel.actions.reopen' : 'scheduler.panel.actions.markDone')}
        </Button>
      );
    case 'reopen':
      return (
        <Button variant="secondary" size="md" onClick={() => onStatus('planned')}>
          {t('scheduler.panel.actions.reopen')}
        </Button>
      );
    case 'unblock':
      return (
        <Button variant="accent" size="md" onClick={() => onStatus('planned')} data-testid="ladder-unblock">
          {t('scheduler.commit.unblock')}
        </Button>
      );
    case 'commit':
      return (
        <>
          <Button variant="ghost" size="sm" onClick={() => onStatus('blocked')} data-testid="ladder-block">
            {t('scheduler.commit.block')}
          </Button>
          <Button variant="accent" size="md" onClick={onCommit} data-testid="ladder-commit">
            {t('scheduler.commit.action')}
          </Button>
        </>
      );
    case 'markDone':
      return (
        <>
          {step.canTakeBack && (
            <Button variant="ghost" size="sm" onClick={onTakeBack} disabled={pending} data-testid="ladder-take-back">
              {t('scheduler.commit.uncommitAction')}
            </Button>
          )}
          <Button variant="accent" size="md" disabled={step.remaining > 0} onClick={onDone} data-testid="ladder-done">
            {t('scheduler.panel.actions.markDone')}
          </Button>
        </>
      );
  }
}
